import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Grid, makeStyles, Typography } from 'transfix-ui/core'
import { getScheduleStops } from 'utils/actions/facility'
import { autoConfirmStop } from 'utils/actions/shipmentStop'
import { scheduleActionsSelector } from 'apps/schedule/utils/selectors/scheduleActions'
import { IStop } from 'apps/schedule/utils/types/stops'
import { log } from 'utils/errorLogger'
import { getUrlParams } from 'utils/getUrlParams'
import { StopCard } from './StopCard'

interface IScheduleProps {
  location: {
    search: string
  }
}

const useScheduleStyles = makeStyles(theme => ({
  root: {
    paddingTop: 72,
  },
  otherStops: {
    padding: theme.spacing(3, 0),
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 0),
  },
}))

export const Schedule = React.memo(({ location }: IScheduleProps) => {
  const styles = useScheduleStyles()
  const dispatch = useDispatch()
  const state = useSelector(scheduleActionsSelector)
  const { confirmSuccess, errors, loadedSchedule, stops } = state
  const {
    appointment_type: apptTypeProp,
    auto_confirm_token: autoConfirmToken,
    end_at_time: endAtTime,
    facility_uuid: facilityUuid,
    shipper_application_id: shipperId,
    start_at_time: startAtTime,
    stop_type: stopTypeProp,
  } = getUrlParams(location.search)

  const stopType = stopTypeProp as IStop['stopType']
  const apptType = apptTypeProp as IStop['appointmentType']

  const { mainStop, otherStops } = stops || {}
  const missingParams = !autoConfirmToken || !shipperId || !facilityUuid || !startAtTime

  // If we haven't loaded the data from the schedule_stops endpoint, do so
  useEffect(() => {
    if (!mainStop && !otherStops && !errors && !loadedSchedule && !missingParams) {
      dispatch(getScheduleStops({ autoConfirmToken, shipperId, stopType, facilityUuid }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If the mainStop isn't confirmed and isn't deprecated ("expired"), then do the auto confirmation
  useEffect(() => {
    if (
      mainStop &&
      !mainStop.confirmed &&
      mainStop.appointmentStatus !== 'confirmed' &&
      !mainStop.isDeprecated
    ) {
      const { stopId } = mainStop
      dispatch(autoConfirmStop(stopId, autoConfirmToken, startAtTime, endAtTime))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainStop])

  // Log error and stop when there is an error
  useEffect(() => {
    if (errors) {
      log('quick_requests_frontend', errors, mainStop)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors])

  return (
    <Grid container className={styles.root}>
      <Grid item xs={1} md={3} />
      <Grid item xs={10} md={6}>
        {!loadedSchedule && <Spinner position='absolute' />}
        {loadedSchedule && (
          <div className='tl'>
            {missingParams && (
              <InlineMessage
                message='Missing required parameter. Unable to make changes. Please reply to the email we sent you.'
                type={INLINE_MESSAGE_VARIANTS.ALERT}
              />
            )}
            {!missingParams && !mainStop && (
              <InlineMessage
                message='Could not locate stop. Please reply to the email we sent you.'
                type={INLINE_MESSAGE_VARIANTS.ALERT}
              />
            )}
            {errors && (
              <InlineMessage
                message='We encountered an error scheduling this shipment, please reply to the email we sent you.'
                type={INLINE_MESSAGE_VARIANTS.ALERT}
              />
            )}
            {mainStop && stopType && apptType && (
              <StopCard
                mainStop={mainStop}
                stopType={stopType}
                apptType={apptType}
                autoConfirmToken={autoConfirmToken}
                confirmSuccess={confirmSuccess}
                isMainStop
              />
            )}
            {otherStops && otherStops.length > 0 && stopType && apptType && (
              <div className={styles.otherStops}>
                <div className={styles.titleContainer}>
                  <Typography variant='h3'>Additional Appointments</Typography>
                  <Typography variant='subtitle1' color='textSecondary'>
                    Are you able to confirm these appointments while you are here?
                  </Typography>
                </div>
                {otherStops?.map(stop => (
                  <StopCard
                    key={stop.stopId}
                    mainStop={stop}
                    stopType={stopType}
                    apptType={apptType}
                    autoConfirmToken={autoConfirmToken}
                    confirmSuccess={confirmSuccess}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </Grid>
    </Grid>
  )
})
